import Button from "@/components/bricks/button/Button";
import { formatTextField, getHeaderActiveColumns } from "../../util/table";
import { Checkbox } from "../formcontrols/InputCheckbox";
import ArrowRightIcon from "../icons/ArrowRightIcon";
import EditColumnsIcon from "../icons/EditColumnsIcon";
import { ModalTrigger } from "../Modal/Modal";
import { PagedLinjeQueryField } from "../../interfaces/linje";
import { PagedKursusQueryField } from "../../interfaces/kursus";
import { PagedFirmaQueryField } from "../../interfaces/firma";
import { PagedPersonsQueryField } from "../../interfaces/personer";
import { get } from "lodash";
import { ITableProps, IRowData, IColumn } from "../../interfaces/table";

const Table = (
  props: ITableProps<
    | PagedPersonsQueryField
    | PagedFirmaQueryField
    | PagedKursusQueryField
    | PagedLinjeQueryField
  >,
) => {
  const {
    columns,
    data,
    keyField,
    onRowClick,
    selectRow,
    className,
    pageInfo,
    sortings,
  } = props;

  const activeColumns = columns.filter((c) => c.active).map((c) => c.field);
  const allOnThisPageAreSelected = data.filter((d) => !d.selected).length === 0;
  const anyOnThisPageAreSelected = data.filter((d) => d.selected).length > 0;

  return (
    <table
      className={className ? `table ${className}` : "table"}
      style={{ userSelect: "none" }}
    >
      <thead>
        <tr>
          {selectRow && (
            <th scope="col" className="action-select">
              {selectRow.toggleSelectAllOnPage && (
                <label className="form-check-label--wrapped">
                  <Checkbox
                    checkboxState={
                      allOnThisPageAreSelected
                        ? "CHECKED"
                        : anyOnThisPageAreSelected
                          ? "INDETERMINATE"
                          : "EMPTY"
                    }
                    aria-label="Vælg"
                    type="checkbox"
                    className="form-check-input"
                    onChange={(e) => {
                      if (selectRow.toggleSelectAllOnPage) {
                        selectRow.toggleSelectAllOnPage(
                          e.target.checked ? "all" : "none",
                        );
                      }
                    }}
                  />
                </label>
              )}
            </th>
          )}

          {getHeaderActiveColumns(columns, sortings)}

          <th scope="col" className="icon-cell">
            <span className="icon-cell-inner icon-cell-inner--btn">
              <ModalTrigger asChild>
                <Button
                  icon
                  transparent
                  aria-label="Rediger kolonner"
                  className="tooltip-center"
                  color="secondary"
                  onClick={() => {}}
                >
                  <EditColumnsIcon width={17} />
                </Button>
              </ModalTrigger>
            </span>
          </th>
        </tr>
        {pageInfo &&
          pageInfo.totalNumberOfRows > pageInfo.pageSize &&
          allOnThisPageAreSelected && (
            <tr>
              <th colSpan={100} className="table-row__message bg-light">
                {!selectRow.isAllSelected && (
                  <>
                    <span className="me-1">
                      Alle <strong>{pageInfo.pageSize}</strong> på denne side er
                      valgt
                    </span>
                    <Button
                      color="primary"
                      outline
                      size="sm"
                      className="ms-3"
                      onClick={() => {
                        selectRow?.selectAll(pageInfo.totalNumberOfRows);
                      }}
                    >
                      Vælg alle <strong>{pageInfo.totalNumberOfRows}</strong> i
                      søgeresultatet
                    </Button>
                  </>
                )}
                {selectRow.isAllSelected && (
                  <>
                    <span className="me-1">
                      Alle {pageInfo.totalNumberOfRows} er valgt
                    </span>
                    <Button
                      size="sm"
                      outline
                      className="ms-1"
                      onClick={() => {
                        selectRow.clearSelection();
                      }}
                    >
                      Ryd valg
                    </Button>
                  </>
                )}
              </th>
            </tr>
          )}
      </thead>

      <tbody>
        {data.map((td) => {
          const key = td[keyField];
          return (
            <tr
              className={td.selected ? "selected" : ""}
              key={key}
              tabIndex={onRowClick ? 0 : -1}
              onClickCapture={(e) => {
                if (
                  ["TD", "TH"].indexOf((e.target as HTMLElement).tagName) < 0
                ) {
                  return false;
                }
                if (onRowClick) {
                  onRowClick(td);
                } else if (selectRow && selectRow.clickToSelect) {
                  selectRow.setSelected(
                    td,
                    td.selected ? false : true,
                    e.shiftKey,
                  );
                }
              }}
              onKeyDownCapture={(e) => {
                if (["TR"].indexOf((e.target as HTMLElement).tagName) < 0) {
                  return false;
                }
                if (e.key === "Enter" || e.key === " ") {
                  if (onRowClick) {
                    onRowClick(td);
                  } else if (selectRow && selectRow.clickToSelect) {
                    selectRow.setSelected(
                      td,
                      td.selected ? false : true,
                      e.shiftKey,
                    );
                  }
                }
              }}
            >
              {selectRow && (
                <th scope="row" className="action-select">
                  <label className="form-check-label--wrapped">
                    <Checkbox
                      checkboxState={td.selected ? "CHECKED" : "EMPTY"}
                      aria-label="Vælg"
                      className="form-check-input"
                      onChange={
                        selectRow && selectRow.clickToSelect
                          ? () => {}
                          : (e) => {
                              e.stopPropagation();
                              selectRow.setSelected(
                                td,
                                td.selected ? false : true,
                                (e.nativeEvent as PointerEvent).shiftKey ??
                                  false,
                              );
                            }
                      }
                    />
                  </label>
                </th>
              )}
              {columns
                .filter((c) => activeColumns.indexOf(c.field) > -1)
                .map((col) => {
                  return <TD key={col.field} col={col} td={td} />;
                })}
              <td className="icon-cell">
                <span className="icon-cell-inner icon-cell-inner--pointer">
                  <ArrowRightIcon width={18} />
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const TD = (props: {
  td: IRowData;
  col: IColumn<
    | PagedPersonsQueryField
    | PagedFirmaQueryField
    | PagedKursusQueryField
    | PagedLinjeQueryField
  >;
}) => {
  const { col, td } = props;

  let data = get(td, col.field);

  if (col.format === "custom" && col.customValue) {
    data = col.customValue(td);
  }

  const textField = formatTextField(data, col.format);

  return <td>{textField}</td>;
};
export default Table;
